import { EmptyMessage } from '../../components/EmptyMessage';
import { ItemCard } from '../../components/ItemCard';
import { PageHeading } from '../../components/PageHeading';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { BookingTopBar } from '../../composites/BookingTopBar';
import { ProductGrid, TopBarRow } from './ProductListTemplate.styles';
import type { ProductListItem, ProductListTemplateProps } from './ProductListTemplate.types';

function buildMeta(item: ProductListItem): string | undefined {
  const meta: string[] = [];
  if (item.duration) meta.push(item.duration);
  if (item.optionName) meta.push(item.optionName);
  return meta.length > 0 ? meta.join(' · ') : undefined;
}

export function ProductListTemplate({
  items,
  onSelectItem,
  isLoading,
  titleLabel = 'Products',
  titleVariant = 'section',
  onBack,
  backLabel,
  progressStep,
  progressTotal,
  actionLabel,
  fromPriceLabel = 'From',
  soldOutLabel = 'Sold out',
  noResultsLabel = 'No products available.',
  noImageLabel = 'No image',
  imageAspectRatio,
  imageMobileAspectRatio,
}: ProductListTemplateProps) {
  const header = (
    <>
      {onBack && (
        <TopBarRow>
          <BookingTopBar
            inset={false}
            onBack={onBack}
            backLabel={backLabel}
            progressStep={progressStep}
            progressTotal={progressTotal}
          />
        </TopBarRow>
      )}
      {titleVariant === 'page' ? (
        <PageHeading title={titleLabel} />
      ) : (
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
      )}
    </>
  );

  if (isLoading && items.length === 0) {
    return (
      <Section>
        {header}
        <ProductGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard
              key={index}
              variant="grid"
              aspectRatio={imageAspectRatio}
              mobileAspectRatio={imageMobileAspectRatio}
            />
          ))}
        </ProductGrid>
      </Section>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Section>
        {header}
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      {header}
      <ProductGrid>
        {items.map((item) => (
          <ItemCard
            key={item.id}
            image={{ src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel }}
            imageAspectRatio={imageAspectRatio}
            imageMobileAspectRatio={imageMobileAspectRatio}
            imageOverlay
            title={item.name}
            meta={item.label || buildMeta(item)}
            statusBadge={item.unavailable ? soldOutLabel : undefined}
            imageBadge={item.promotionName || undefined}
            price={
              !item.unavailable && item.price
                ? {
                    amount: item.price.amount,
                    currencyCode: item.price.currencyCode,
                    label: fromPriceLabel,
                    discount: item.discount,
                  }
                : undefined
            }
            unavailable={item.unavailable}
            ariaLabel={item.name}
            onSelect={() => onSelectItem(item.id)}
            selectButtonLabel={actionLabel && !item.unavailable ? actionLabel : undefined}
          />
        ))}
      </ProductGrid>
    </Section>
  );
}
